// biome-ignore-all lint:no-explicit-any
// TODO; make type safe

import { ExternalDataService, StringService } from '@repo/roche-xx-shared';
import {
  LinkTarget,
  RocheUIAccordion,
  RocheUISideBySide,
  RocheUISideBySideVerticalAlignment,
  RocheUITextLink,
} from '@rocheglobal/roche-ui';
import React, { useEffect, useState } from 'react';
import { RocheLoadingSpinner } from './roche-loader-spinner';

interface SafetyReportingResponse {
  Items: Record<string, string>[];
  Page: number;
  Total: number;
}

const sortAlphabeticallyByValue = (key: string) => (a: unknown, b: unknown) => a[key]?.localeCompare(b[key]);
const buildDetails = (object: Record<string, string>, keyPattern: string): string[] =>
  Object.keys(object)
    .sort()
    .filter((key) => key.includes(keyPattern) && object[key] && object[key].trim() !== '')
    .map((key) => object[key]);

export const RocheSafetyReportingOverview = ({ slot }: any): JSX.Element => {
  const [state, setState] = useState({ isFetching: false, items: [] });

  useEffect(() => {
    setState((prevState) => ({ ...prevState, isFetching: true }));
    ExternalDataService.getSafetyReporting()
      .then((result: SafetyReportingResponse) => {
        setState((prevState) => ({ ...prevState, items: result.Items }));
      })
      .finally(() => setState((prevState) => ({ ...prevState, isFetching: false })));
  }, []);

  if (state.isFetching) {
    return <RocheLoadingSpinner />;
  }

  if (!state.items.length) {
    return <roche-empty-state></roche-empty-state>;
  }

  const sortedItems = state.items.sort(sortAlphabeticallyByValue('country_name'));
  const sections = sortedItems.map((location) => ({
    id: StringService.urlify(location.country_name),
    headline: location.country_name,
  }));

  return (
    <RocheUIAccordion sections={sections} slot={slot}>
      {sortedItems.map((location, index) => {
        const addressLines = buildDetails(location, 'address_line').map((address) => `${address}<br/>`);
        const [name, ...address] = addressLines;
        const telephoneNumbers = buildDetails(location, 'telephone_number');
        const faxNumbers = buildDetails(location, 'fax_number');
        const emails = buildDetails(location, 'email');
        const mobileNumbers = buildDetails(location, 'mobile_number');

        return (
          <div key={index}>
            <roche-headline level={3} style={{ marginTop: 0 }}>
              <h3 dangerouslySetInnerHTML={{ __html: name }}></h3>
            </roche-headline>

            <RocheUISideBySide equalRatio verticalAlignment={RocheUISideBySideVerticalAlignment.TOP}>
              <roche-richtext>
                <p
                  dangerouslySetInnerHTML={{
                    __html: address.join(''),
                  }}
                ></p>
              </roche-richtext>

              <address style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {telephoneNumbers.map((number, i) => (
                  <RocheUITextLink
                    key={i}
                    href={`tel:${number}`}
                    text={number}
                    leftIcon='phone'
                    target={LinkTarget.BLANK}
                  />
                ))}
                {faxNumbers.map((number, i) => (
                  <RocheUITextLink
                    key={i}
                    href={`tel:${number}`}
                    text={number}
                    leftIcon='fax'
                    target={LinkTarget.BLANK}
                  />
                ))}
                {emails.map((email, i) => (
                  <RocheUITextLink
                    key={i}
                    href={`mailto:${email}`}
                    text={email}
                    leftIcon='email'
                    target={LinkTarget.BLANK}
                  />
                ))}
                {mobileNumbers.map((number, i) => (
                  <RocheUITextLink
                    key={i}
                    href={`tel:${number}`}
                    text={number}
                    leftIcon='mobile'
                    target={LinkTarget.BLANK}
                  />
                ))}
              </address>
            </RocheUISideBySide>
          </div>
        );
      })}
    </RocheUIAccordion>
  );
};
